import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import UndoIcon from '@material-ui/icons/Undo';
import { IconArrowBarRight, IconClipboardCheck } from '@tabler/icons-react';
import React from 'react';

import ActionsPopup from 'src/components/general/actionsPopup';
import { SubType } from 'src/events/visitTypes';
import { COUNTERSIGN, RN_CAN_CREATE_PRESCRIPTION_TASKS } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { PrescriptionTaskStatus } from 'src/prescriptionTasksQueue/PrescriptionTaskStatus';
import { CLINICIAN_EVENT_SUBTYPES, EVENT_STATUSES } from 'src/shared/util/events';
import { ProviderRole } from 'src/stores/users/userType';

const PRESCRIPTION_TASK_SUPPORTED_EVENTS: SubType[] = [
  'bridge_prescription_request',
  'patient_bridge_submission',
  'registered_nurse',
  'registered_nurse_bridge_care',
  'registered_nurse_drop_in_followup',
  'registered_nurse_drop_in_reengagement',
  'registered_nurse_reengagement',
  'registered_nurse_triage_note',
];

const EventStatusActionsPopup = ({
  event,
  user,
  prescriptionTask,
  parentElement,
  onCancel,
  onClose,
  onComplete,
  onReschedule,
  onUncomplete,
  onCountersign,
  onUncountersign,
  onCreatePrescriptionTask,
  onCompletePrescriptionTask,
}) => {
  const isCountersignEnabled = useFeatureFlag(COUNTERSIGN);

  const clinicianSignatureRequired = CLINICIAN_EVENT_SUBTYPES.includes(event.subType);
  const userIsAttendee = !!event.attendees?.find(attendee => attendee.id === user.id);

  const unauthorizedSigner =
    !userIsAttendee || (clinicianSignatureRequired && user.teamRole !== ProviderRole.Clinician);

  let signingDisabledReason;
  if (event.status === EVENT_STATUSES.CANCELED) {
    signingDisabledReason = 'Canceled visits do not need to be signed & completed';
  } else if (unauthorizedSigner) {
    signingDisabledReason = clinicianSignatureRequired
      ? 'This clinical visit can only be signed by a clinician who attended'
      : 'A visit can only be signed by a care team member who attended';
  }

  const isCompletedAndSigned = event.status === EVENT_STATUSES.COMPLETED && event.signedBy;

  const userIsRn = user.teamRole === ProviderRole.RegisteredNurse;
  const eventSupportsPrescriptionTask = PRESCRIPTION_TASK_SUPPORTED_EVENTS.includes(event.subType);
  const rnCanCreatePrescriptionTasksFlagEnabled = useFeatureFlag(RN_CAN_CREATE_PRESCRIPTION_TASKS);
  const canCreatePrescriptionTask =
    rnCanCreatePrescriptionTasksFlagEnabled &&
    !prescriptionTask &&
    eventSupportsPrescriptionTask &&
    userIsRn;

  const canCompletePrescriptionTask =
    !!prescriptionTask &&
    prescriptionTask.status === PrescriptionTaskStatus.Accepted &&
    user.teamRole === ProviderRole.Clinician;

  return (
    <ActionsPopup
      parentElement={parentElement}
      onClose={onClose}
      actions={[
        {
          isDisplayed: true,
          isDisabled: [EVENT_STATUSES.RESCHEDULED, EVENT_STATUSES.COMPLETED].includes(event.status),
          onClick: () => onCancel(event),
          iconComponent: DeleteIcon,
          label: 'Cancel...',
        },
        {
          isDisplayed: true,
          isDisabled: event.status === EVENT_STATUSES.COMPLETED,
          onClick: () => onReschedule(event),
          iconComponent: CalendarTodayIcon,
          label: 'Reschedule...',
        },
        {
          isDisplayed: event.status !== EVENT_STATUSES.COMPLETED,
          isDisabled: event.status === EVENT_STATUSES.RESCHEDULED || !!signingDisabledReason,
          onClick: () => onComplete(event),
          iconComponent: CheckCircleIcon,
          label: 'Sign & Complete',
          tooltip: signingDisabledReason,
        },
        {
          isDisplayed: event.status === EVENT_STATUSES.COMPLETED,
          onClick: () => onUncomplete(event),
          iconComponent: UndoIcon,
          label: 'Unsign & Un-Complete',
        },
        {
          isDisplayed: isCountersignEnabled && isCompletedAndSigned && !event.countersignedBy,
          onClick: () => onCountersign(event),
          iconComponent: CheckCircleIcon,
          label: 'Countersign',
        },
        {
          isDisplayed: isCountersignEnabled && isCompletedAndSigned && event.countersignedBy,
          onClick: () => onUncountersign(event),
          iconComponent: UndoIcon,
          label: 'Un-countersign',
        },
        {
          isDisplayed: canCreatePrescriptionTask,
          onClick: () => onCreatePrescriptionTask(event),
          iconComponent: IconArrowBarRight,
          label: 'Send to Clinician Queue',
        },
        {
          isDisplayed: canCompletePrescriptionTask,
          onClick: onCompletePrescriptionTask,
          iconComponent: IconClipboardCheck,
          label: 'Complete Prescription Task',
        },
      ]}
    />
  );
};

export default EventStatusActionsPopup;
